import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";

export const Route = createFileRoute("/access-requests")({
  component: AccessRequestsPage,
});

interface AccessRequest {
  id: string;
  email: string;
  email_verified: number;
  status: string;
  requested_at: string;
}

function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem("pic_auth_session");
    if (!stored) return null;
    return JSON.parse(stored)?.sessionId ?? null;
  } catch {
    return null;
  }
}

function AccessRequestsPage() {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const sessionId = getSessionId();

  const fetchRequests = async () => {
    if (!sessionId) { setError("Not authenticated"); setIsLoading(false); return; }
    try {
      const res = await fetch("/api/v1/auth/access-requests", {
        headers: { "X-Session-ID": sessionId },
      });
      const result = await res.json() as { success: boolean; data?: AccessRequest[]; error?: string };
      if (!result.success) { setError(result.error ?? "Failed to load"); return; }
      setRequests(result.data ?? []);
    } catch {
      setError("Failed to load requests");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleAction = async (userId: string, action: "approve" | "deny") => {
    if (!sessionId) return;
    setProcessing(userId);
    try {
      const res = await fetch("/api/v1/auth/access-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Session-ID": sessionId },
        body: JSON.stringify({ userId, action }),
      });
      const result = await res.json() as { success: boolean; data?: { email: string }; error?: string };
      if (!result.success) { setToast(result.error ?? "Failed"); return; }
      setToast(action === "approve" ? `${result.data?.email} approved` : `${result.data?.email} denied`);
      setRequests((prev) => prev.filter((r) => r.id !== userId));
    } catch {
      setToast("Something went wrong");
    } finally {
      setProcessing(null);
      setTimeout(() => setToast(null), 3000);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl font-medium" style={{ color: "#171717" }}>Access Requests</h1>
        <p className="text-sm mt-1" style={{ color: "#525252" }}>
          Team members who have requested access to your brand account.
        </p>
      </div>

      {toast && (
        <div className="px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: "rgba(0,251,169,0.12)", color: "#171717" }}>
          {toast}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#00FBA9", borderTopColor: "transparent" }} />
        </div>
      )}

      {error && (
        <div className="px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: "rgba(255,76,0,0.08)", color: "#FF4C00" }}>
          {error}
        </div>
      )}

      {!isLoading && !error && requests.length === 0 && (
        <div className="text-center py-16" style={{ color: "#A9A9A9" }}>
          <p className="text-sm">No pending access requests.</p>
        </div>
      )}

      {requests.length > 0 && (
        <div className="divide-y rounded-lg border overflow-hidden" style={{ borderColor: "rgba(169,169,169,0.2)" }}>
          {requests.map((req) => (
            <div key={req.id} className="flex items-center justify-between px-4 py-4 bg-white">
              <div className="space-y-0.5">
                <p className="text-sm font-medium" style={{ color: "#171717" }}>{req.email}</p>
                <div className="flex items-center gap-2">
                  <p className="text-xs" style={{ color: "#A9A9A9" }}>
                    {new Date(req.requested_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                  {req.email_verified ? (
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(0,251,169,0.12)", color: "#009e6a" }}>
                      Email verified
                    </span>
                  ) : (
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(169,169,169,0.12)", color: "#A9A9A9" }}>
                      Pending verification
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleAction(req.id, "deny")}
                  disabled={processing === req.id}
                  className="px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-50"
                  style={{ borderColor: "rgba(169,169,169,0.3)", color: "#525252" }}
                >
                  Deny
                </button>
                <button
                  onClick={() => handleAction(req.id, "approve")}
                  disabled={processing === req.id}
                  className="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: "#00FBA9", color: "#171717" }}
                >
                  {processing === req.id ? "..." : "Approve"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
